import { jsPDF } from 'jspdf';
import { formatCurrency, formatDate } from './useUtils'; // Ajusta la ruta relativa si es necesario

/**
 * Genera el PDF en formato POS (80mm) para facturas.
 * @param {Object} factura - Objeto completo de la factura (header y lines).
 * @param {Object} companyConfig - Objeto de configuración de la empresa.
 * @param {boolean} isReimpresion - Si es true, añade la marca de REIMPRESION.
 * @returns {Promise<void>} - Devuelve una promesa que se resuelve cuando el PDF está listo.
 */
// 🛑 1. La función principal useInvoicePrinter NO ES ASYNC, pero retorna una Promesa.
export function useInvoicePrinter(factura, companyConfig, isReimpresion = false) {
    // 🛑 2. El ejecutor de la promesa NO ES ASYNC, pero definimos una función interna async.
    return new Promise((resolve) => {

        const { header, lines } = factura;
        const {
            currency, client_name, client_nit,
            subtotal, total_taxes, total, total_discount, date_issued, full_number, invoice_type,
            sub_total_brut
        } = header;

        // --- Configuración de la Empresa ---
        const companyLogo = companyConfig?.logo_url || '';
        const companyName = companyConfig?.emitter_name || 'Mi Empresa POS';
        const companyNit = companyConfig?.emitter_nit || 'N/A';
        const companyCountry = companyConfig?.country || 'N/A';
        const companyCity = companyConfig?.city || 'N/A';
        const companyAddress = companyConfig?.address || 'N/A';
        const companyWeb = companyConfig?.website || '';
        const companyPhone = companyConfig?.phone_number || '';
        const companyEmail = companyConfig?.email || '';
        const currencySymbol = companyConfig?.currency_symbol || '$';
        const footerNotes = companyConfig?.footer_notes || "Gracias por su compra.";
        const totalLines = lines.length;
        const user = localStorage.getItem('userName') || 'Cajero';

        if (totalLines === 0) {
            alert('La factura no tiene líneas de producto para imprimir.');
            return resolve(); // Resolvemos de inmediato si no hay nada que imprimir
        }

        // 🛑 Función ASYNC envuelve la lógica del PDF para usar 'await' 🛑
        const doPrint = async () => {
            // --- Dimensiones y Setup de jsPDF ---
            const logoHeight = companyLogo ? 15 : 0; // Espacio reservado para el logo (15mm)
            const headerHeight = 30 + logoHeight;
            const footerHeight = 70;
            const bodyLineHeight = 5;
            const bodyHeight = totalLines * bodyLineHeight * 2;
            const paperHeight = headerHeight + bodyHeight + footerHeight + (isReimpresion ? 15 : 0);

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [80, paperHeight]
            });

            const marginLeft = 1;
            const pageWidth = doc.internal.pageSize.getWidth();
            let yPosition = 5;

            // LÓGICA DE CARGA ASÍNCRONA DEL LOGO
            if (companyLogo) {
                try {
                    // Función auxiliar para cargar la imagen como base64 o Blob
                    const imageBase64 = await new Promise((res) => {
                        const img = new Image();
                        img.crossOrigin = 'Anonymous';
                        img.onload = () => {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.width;
                            canvas.height = img.height;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0);
                            res(canvas.toDataURL('image/jpg'));
                        };
                        img.onerror = (e) => {
                            console.error("Fallo al cargar el logo:", e);
                            res(null); // Continuar sin logo si falla
                        };
                        img.src = companyLogo;
                    });

                    if (imageBase64) {
                        const logoWidth = 25;
                        const logoX = (pageWidth - logoWidth) / 2;

                        doc.addImage(imageBase64, 'JPG', logoX, yPosition, logoWidth, logoHeight - 2);
                        yPosition += logoHeight;
                    }
                } catch (e) {
                    console.error("Error al añadir logo al PDF:", e);
                    yPosition += 15; // Espacio de fallback
                }
            } else {
                yPosition += 10; // Pequeño margen superior si no hay logo
            }
            // FIN LÓGICA LOGO

            // === 1. HEADER DE EMPRESA ===
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            yPosition += 5;
            doc.text(companyName, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 5;

            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            doc.text(`NIT: ${companyNit}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 4;
            doc.text(`DIRECCIÓN: ${companyAddress} ${companyCity}, ${companyCountry}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 4;
            if (companyPhone && companyPhone.toUpperCase() !== 'N/A') {
                doc.text(`TELÉFONO: ${companyPhone}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 4;
            }
            if (companyEmail && companyEmail.toUpperCase() !== 'N/A') {
                doc.text(`EMAIL: ${companyEmail}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 4;
            }
            if (companyWeb && companyWeb.toUpperCase() !== 'N/A') {
                doc.text(`${companyWeb}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 4;
            }
            doc.setLineWidth(0.5);
            doc.line(marginLeft, yPosition, pageWidth - marginLeft, yPosition);
            yPosition += 5;

            // === 2. INFO DE FACTURA Y CLIENTE ===
            doc.setFontSize(8);
            doc.setFont(undefined, 'bold');
            doc.text(`${(invoice_type || factura.invoice_type)}: ${full_number || factura.full_number}`, marginLeft, yPosition);
            doc.text(`FECHA: ${formatDate(date_issued || factura.fecha)}`, pageWidth - marginLeft, yPosition, { align: 'right' });
            yPosition += 4;

            doc.setFont(undefined, 'normal');
            doc.text(`CLIENTE: ${client_name}`, marginLeft, yPosition);
            yPosition += 4;
            doc.text(`NIT: ${client_nit}`, marginLeft, yPosition);
            yPosition += 4;
            doc.text(`CAJERO: ${user}`, marginLeft, yPosition);
            yPosition += 6;

            // === 3. TABLA DE DETALLE (DIBUJO MANUAL) ===
            const colDesc = 35;
            const colCant = 10;

            doc.setFontSize(7);
            doc.setFont(undefined, 'bold');
            doc.text('Descripción', marginLeft, yPosition);
            doc.text('Cant.', marginLeft + colDesc, yPosition, { align: 'right' });
            doc.text('P/U', marginLeft + colDesc + colCant, yPosition, { align: 'right' });
            doc.text('Total', pageWidth - marginLeft, yPosition, { align: 'right' });
            yPosition += 2;

            doc.setLineWidth(0.3);
            doc.line(marginLeft, yPosition, pageWidth - marginLeft, yPosition);
            yPosition += 3;

            doc.setFont(undefined, 'normal');
            lines.forEach(line => {
                const lineDiscount = line.discount || 0;
                const rawText = line.description || line.nombre || '';
                const productDescription = (typeof rawText === 'string' && rawText.length > 17)
                    ? rawText.substring(0, 17) + '...'
                    : rawText;

                // Línea 1: Nombre del producto y cantidades/precios
                doc.setFontSize(8);
                doc.text(productDescription, marginLeft, yPosition);
                doc.text(line.quantity.toFixed(0) + ' -', marginLeft + colDesc - 2, yPosition, { align: 'right' });
                doc.text(formatCurrency(line.unit_price, currency, currencySymbol), marginLeft + colDesc + colCant, yPosition, { align: 'right' });
                doc.text(formatCurrency(line.line_total, currency, currencySymbol), pageWidth - marginLeft, yPosition, { align: 'right' });
                yPosition += 4;

                // Línea 2: Descuento e Impuesto (Detalle de costes)
                if (lineDiscount > 0 || line.tax_rate > 0) {
                    doc.setFontSize(7);
                    if (lineDiscount > 0) {
                        doc.text(`Desc. (-${formatCurrency(lineDiscount, currency, currencySymbol)})`, marginLeft + 5, yPosition);
                    }

                    const taxAmountLine = (line.tax_rate);
                    const baseImponibleLine = line.sub_total_brut;

                    doc.text(`Sub: ${formatCurrency(baseImponibleLine, currency, currencySymbol)}`, marginLeft + 35, yPosition);
                    doc.text(`Imp: ${formatCurrency(taxAmountLine, currency, currencySymbol)}`, pageWidth - marginLeft, yPosition, { align: 'right' });
                    yPosition += 4;
                } else {
                    yPosition += 1;
                }
            });

            // Línea separadora final de la tabla
            doc.setLineWidth(0.3);
            doc.line(marginLeft, yPosition, pageWidth - marginLeft, yPosition);
            yPosition += 4;

            // === 4. TOTALES ===
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');

            // Subtotal Bruto
            doc.text('SUBTOTAL:', marginLeft, yPosition);
            doc.text(formatCurrency(sub_total_brut || 0, currency, currencySymbol), pageWidth - marginLeft, yPosition, { align: 'right' });
            yPosition += 4;

            // Descuento
            if (total_discount > 0) {
                doc.text('DESCUENTO:', marginLeft, yPosition);
                doc.text(`-${formatCurrency(total_discount, currency, currencySymbol)}`, pageWidth - marginLeft, yPosition, { align: 'right' });
                yPosition += 4;
            }
            if (total_discount > 0) {
                // Subtotal Neto (Base Imponible)
                doc.text('SUBTOTAL NETO:', marginLeft, yPosition);
                doc.text(formatCurrency(subtotal, currency, currencySymbol), pageWidth - marginLeft, yPosition, { align: 'right' });
                yPosition += 4;
            }

            // IVA Total
            doc.text(`IMPUESTO:`, marginLeft, yPosition);
            doc.text(formatCurrency(total_taxes, currency, currencySymbol), pageWidth - marginLeft, yPosition, { align: 'right' });
            yPosition += 5;

            // Total Final
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('TOTAL A PAGAR:', marginLeft, yPosition);
            doc.text(formatCurrency(total, currency, currencySymbol), pageWidth - marginLeft, yPosition, { align: 'right' });
            yPosition += 6;

            // Línea separadora
            doc.setLineWidth(0.5);
            doc.line(marginLeft, yPosition, pageWidth - marginLeft, yPosition);
            yPosition += 5;

            // === 5. PIE DE PÁGINA ===
            doc.setFontSize(6);
            doc.setFont(undefined, 'normal');

            const footerLinesArray = footerNotes.split('\r\n').map(line => line.trim()).filter(line => line.length > 0);

            if (footerLinesArray.length > 0) {
                footerLinesArray.forEach(text => {
                    doc.text(text, pageWidth / 2, yPosition, { align: 'center' });
                    yPosition += 4;
                });
            } else {
                doc.text(footerNotes, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 4;
            }

            // === MARCA DE REIMPRESION ===
            if (isReimpresion) {
                yPosition += 10;
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('**REIMPRESION**', pageWidth / 2, yPosition, { align: 'center' });
            }

            // Abrir PDF
            // 1. Generar el Blob
            const blob = doc.output('blob');

            // 2. Crear una URL segura para el Blob
            const blobUrl = URL.createObjectURL(blob);

            // 3. Crear un enlace (anchor) temporal
            const a = document.createElement('a');
            a.href = blobUrl;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.download = `Factura_${full_number || factura.full_number}.pdf`; // Nombre del archivo

            // 4. Simular un clic para abrir/descargar (esto funciona mejor en Safari)
            a.click();

            // 5. Liberar la URL del Blob
            URL.revokeObjectURL(blobUrl);
            resolve();
        }

        // Ejecutar la función asíncrona que contiene toda la lógica del PDF
        doPrint();
    });
}